import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Users, UserCheck, Trophy, PieChart, Activity } from 'lucide-react';
import { StatCard, WidgetPanel } from './DashboardShared';
import { orgUsersOf, salesOf } from '../../../data/store';

export const UsersModule = ({ org }) => {
  const metrics = useMemo(() => {
    const users = orgUsersOf(org.id);
    const sales = salesOf(org.id);
    const today = new Date().toDateString();

    const todaySales = sales.filter(s => new Date(s.date).toDateString() === today);
    const activeStaffIds = new Set();
    
    // Calculate staff performance
    const performance = {};
    users.forEach(u => {
      performance[u.id] = { name: u.name, revenue: 0, bills: 0, role: u.role };
    });

    sales.forEach(s => {
      if (performance[s.billerId]) {
        performance[s.billerId].revenue += s.total;
        performance[s.billerId].bills += 1;
      }
    });

    todaySales.forEach(s => {
      if (s.billerId) activeStaffIds.add(s.billerId);
    });

    const topPerformers = Object.values(performance)
      .sort((a,b) => b.revenue - a.revenue)
      .slice(0, 5)
      .filter(p => p.revenue > 0 || p.bills > 0);

    const staffByRole = {};
    users.forEach(u => {
      staffByRole[u.role] = (staffByRole[u.role] || 0) + 1;
    });

    return { totalStaff: users.length, activeToday: activeStaffIds.size, topPerformers, staffByRole, users };
  }, [org.id]);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.15em] text-[#0d0d0d] border-b border-[var(--theme-tertiary)] pb-3 mt-4">
        <Users size={16} className="text-[var(--theme-primary)]" />
        Staff Management
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Users} label="Total Staff" value={metrics.totalStaff} iconColor="#14b8a6" />
        <StatCard icon={UserCheck} label="Active Today" value={metrics.activeToday} suffix="logged in / billed" iconColor="#f59e0b" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <WidgetPanel title="Top Performers (Lifetime)" className="lg:col-span-2">
          <ul className="divide-y divide-[var(--theme-tertiary)]">
            {metrics.topPerformers.length === 0 && <li className="py-6 text-center text-xs font-semibold uppercase tracking-[0.14em] text-slate-300">No sales data yet</li>}
            {metrics.topPerformers.map((staff, idx) => (
              <li key={idx} className="py-3 flex items-center justify-between text-[11px] group cursor-pointer hover:bg-slate-50 px-3 -mx-3 rounded-lg transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs ${idx === 0 ? 'bg-amber-100 text-amber-600' : idx === 1 ? 'bg-slate-200 text-slate-600' : idx === 2 ? 'bg-orange-100 text-orange-600' : 'bg-slate-50 border border-slate-200 text-slate-400'}`}>
                    #{idx + 1}
                  </div>
                  <div>
                    <p className="font-bold text-[#0d0d0d] text-sm">{staff.name}</p>
                    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">{staff.role}</p>
                  </div>
                </div>
                <div className="flex flex-col items-end">
                  <p className="font-extrabold text-[var(--theme-primary)] text-sm">₹{staff.revenue.toFixed(2)}</p>
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mt-0.5">{staff.bills} bills</span>
                </div>
              </li>
            ))}
          </ul>
        </WidgetPanel>

        <div className="flex flex-col gap-4">
          <WidgetPanel title="Staff by Role">
            <ul className="divide-y divide-[var(--theme-tertiary)]">
              {Object.entries(metrics.staffByRole).map(([role, count], idx) => (
                <li key={role} className="py-2.5 flex items-center justify-between text-xs">
                  <span className="font-bold text-slate-600 uppercase tracking-widest pr-4">{role.replace(/-/g, ' ')}</span>
                  <span className="font-bold text-[#0d0d0d] bg-slate-100 px-2 py-0.5 rounded">{count}</span>
                </li>
              ))}
            </ul>
          </WidgetPanel>
          
          <WidgetPanel title="Recent Activity">
             <div className="flex-1 flex flex-col items-center justify-center min-h-[100px] text-center gap-2 text-slate-400">
               <Activity size={24} className="opacity-30" />
               <span className="text-[10px] font-bold uppercase tracking-[0.15em]">Activity Log Available Soon</span>
             </div>
          </WidgetPanel>
        </div>
      </div>
    </div>
  );
};

UsersModule.propTypes = {
  org: PropTypes.object.isRequired,
};
